/* =========================================================
   Octocurl Variant Filter (Dawn 15+ stable)
   Dropdowns
   Option 1 label = "Size"
   Option 2 label = "Fabric"
   Goal: When Size changes, Fabric dropdown only shows Fabric values
         that actually exist for that Size (even if sold out).
   Note: Uses hidden+disabled (Chrome hides; Safari may grey out).
========================================================= */

(() => {
  let productCache = new Map(); // handle -> product json
  let isAutoSelecting = false;

  function norm(s) {
    return String(s || '').replace(/\s*-\s*Unavailable\s*$/i, '').trim();
  }

  function getProductHandle() {
    const canonical = document.querySelector('link[rel="canonical"]')?.href || window.location.href;
    const m = canonical.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  async function fetchProductJson(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);

    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) return null;
      const json = await res.json();
      productCache.set(handle, json);
      return json;
    } catch {
      return null;
    }
  }

  function getDropdownPairs(vs) {
    const wraps = [...vs.querySelectorAll('.product-form__input--dropdown')];
    const items = wraps
      .map((wrap, i) => {
        const label = wrap.querySelector('label')?.textContent?.trim() || '';
        const select = wrap.querySelector('select');
        return { i, label, select };
      })
      .filter((x) => x.select);

    if (items.length >= 2) return items;

    // fallback
    const selects = [...vs.querySelectorAll('select')];
    return selects.map((select, i) => ({ i, label: '', select }));
  }

  function getSizeAndFabricSelect(vs) {
    const items = getDropdownPairs(vs);

    const sizeItem = items.find((x) => /^size$/i.test(x.label)) || items[0];
    const fabricItem = items.find((x) => /^fabric$/i.test(x.label)) || items[1];

    return {
      sizeSelect: sizeItem?.select || null,
      fabricSelect: fabricItem?.select || null,
    };
  }

  function buildValidFabricMapFromProductJson(productJson) {
    const map = new Map(); // size -> Set(fabric)

    (productJson?.variants || []).forEach((v) => {
      const opts = v.options || [];
      const size = norm(opts[0]);
      const fabric = norm(opts[1]);
      if (!size || !fabric) return;

      if (!map.has(size)) map.set(size, new Set());
      map.get(size).add(fabric);
    });

    return map;
  }

  function applyFilter(vs, validMap) {
    if (isAutoSelecting) return;

    const { sizeSelect, fabricSelect } = getSizeAndFabricSelect(vs);
    if (!sizeSelect || !fabricSelect) return;

    const selectedSize = norm(sizeSelect.value);
    if (!selectedSize) return;

    const validFabrics = validMap.get(selectedSize) || new Set();

    let firstValidValue = null;

    [...fabricSelect.options].forEach((opt) => {
      const v = norm(opt.value || opt.textContent);

      // leave placeholder alone
      if (!v) return;

      const exists = validFabrics.has(v);

      // hide + disable non-existent combos
      opt.hidden = !exists;
      opt.disabled = !exists;

      if (exists && !firstValidValue) firstValidValue = opt.value;
    });

    // if selected fabric becomes invalid, auto-select first valid
    const currentFabric = norm(fabricSelect.value);
    if (currentFabric && !validFabrics.has(currentFabric) && firstValidValue) {
      isAutoSelecting = true;
      fabricSelect.value = firstValidValue;
      fabricSelect.dispatchEvent(new Event('change', { bubbles: true }));
      isAutoSelecting = false;
    }
  }

  async function init() {
    const handle = getProductHandle();
    const productJson = await fetchProductJson(handle);
    if (!productJson) return;

    const validMap = buildValidFabricMapFromProductJson(productJson);

    const runAll = () => {
      document.querySelectorAll('variant-selects').forEach((vs) => applyFilter(vs, validMap));
    };

    runAll();

    // capture-phase change listener so it keeps working after Dawn swaps DOM
    document.addEventListener(
      'change',
      (e) => {
        const vs = e.target?.closest?.('variant-selects');
        if (!vs) return;
        applyFilter(vs, validMap);
      },
      true
    );

    // re-run after Dawn re-renders sections
    new MutationObserver(runAll).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();